import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useLanguage } from '@/lib/context';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Loader2, CheckCircle, Clock, Zap, Users, 
  Calendar, MapPin, ArrowLeft, Crown, MessageSquare,
  Heart, Sparkles, Shield
} from 'lucide-react';
import {
  ReassuranceMessage,
  MotivationalTip,
  FriendlyLoader,
  EmptyStateEncouragement
} from '@/components/UserLove';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Booking Status Page - User Love Edition
 * Shows booking status and provider responses with emotional engagement
 */
const BookingStatusPage = () => {
  const { t, language } = useLanguage();
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [polling, setPolling] = useState(true);
  const [showInitialSuccess, setShowInitialSuccess] = useState(true);

  useEffect(() => {
    if (!bookingId || !token) {
      setError('Invalid booking link');
      setLoading(false);
      return;
    }

    fetchStatus();

    // Poll every 10 seconds for updates
    const interval = setInterval(() => {
      if (polling) fetchStatus();
    }, 10000);

    // Hide success message after 5 seconds
    const successTimer = setTimeout(() => {
      setShowInitialSuccess(false);
    }, 5000);

    return () => {
      clearInterval(interval);
      clearTimeout(successTimer);
    };
  }, [bookingId, token]);

  const fetchStatus = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/bookings/status/${bookingId}?token=${token}`
      );

      if (!response.ok) {
        throw new Error('Booking not found');
      }

      const data = await response.json();
      setBooking(data);

      // Stop polling if booking is completed or expired
      if (['confirmed', 'completed', 'expired', 'cancelled'].includes(data.status)) {
        setPolling(false);
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 flex items-center justify-center">
        <FriendlyLoader language={language} context="sending" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="max-w-md w-full mx-4 border-red-200">
            <CardContent className="pt-8 text-center">
              <p className="text-red-600 mb-4">{error}</p>
              <Link to="/booking">
                <Button variant="outline">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {t('booking.status.backHome') || 'Back to Home'}
                </Button>
              </Link>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  const statusConfig = {
    pending: {
      color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      icon: Clock,
      label: t('booking.status.pending') || 'Awaiting Responses',
      reassuranceType: 'waiting'
    },
    confirmed: {
      color: 'bg-green-100 text-green-800 border-green-200',
      icon: CheckCircle,
      label: t('booking.status.confirmed') || 'Confirmed',
      reassuranceType: 'confirmed'
    },
    alternative: {
      color: 'bg-blue-100 text-blue-800 border-blue-200',
      icon: Calendar,
      label: t('booking.status.alternative') || 'Alternative Proposed',
      reassuranceType: 'matched'
    },
    declined: {
      color: 'bg-red-100 text-red-800 border-red-200',
      icon: Clock,
      label: t('booking.status.declined') || 'Declined',
      reassuranceType: 'waiting'
    },
    expired: {
      color: 'bg-gray-100 text-gray-800 border-gray-200',
      icon: Clock,
      label: t('booking.status.expired') || 'Expired',
      reassuranceType: 'waiting'
    }
  };

  const currentStatus = statusConfig[booking?.status] || statusConfig.pending;
  const StatusIcon = currentStatus.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50">
      {/* Header */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-orange-500 hover:text-orange-600 transition">
            KEIKKA
          </Link>
        </div>
      </nav>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Initial Success Celebration */}
        <AnimatePresence>
          {showInitialSuccess && booking?.status === 'pending' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-6"
            >
              <ReassuranceMessage type="submitted" language={language} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Status Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="mb-6 overflow-hidden" data-testid="booking-status-card">
            {/* Status Banner */}
            <div className={`px-6 py-3 ${currentStatus.color.replace('text-', 'bg-').replace('800', '50')}`}>
              <div className="flex items-center gap-2">
                <StatusIcon className={`h-5 w-5 ${currentStatus.color.split(' ')[1]}`} />
                <span className={`font-medium ${currentStatus.color.split(' ')[1]}`}>
                  {currentStatus.label}
                </span>
              </div>
            </div>

            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    {t('booking.status.title') || 'Booking Status'}
                  </CardTitle>
                  <CardDescription>
                    {t('booking.status.bookingId') || 'Booking ID'}: <span className="font-mono">{booking?.booking_id}</span>
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                {/* Booking Details */}
                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl">
                  <div className="p-2 bg-white rounded-lg shadow-sm">
                    <Calendar className="h-5 w-5 text-orange-500" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{booking?.category}</p>
                    <p className="text-sm text-muted-foreground">
                      {booking?.preferred_date} {booking?.preferred_time && `• ${booking.preferred_time}`}
                    </p>
                  </div>
                </div>

                {/* Quick Response Badge */}
                {booking?.quick_response && (
                  <motion.div 
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="flex items-center gap-3 p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl border border-orange-200"
                  >
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <Zap className="h-5 w-5 text-orange-500" />
                    </div>
                    <div>
                      <p className="font-medium text-orange-800">
                        {t('booking.status.quickResponse') || 'Quick Response Active'}
                      </p>
                      <p className="text-sm text-orange-600">
                        {t('booking.status.quickResponseDesc') || 'Providers notified via SMS'}
                      </p>
                    </div>
                    <Badge className="ml-auto bg-gradient-to-r from-orange-400 to-yellow-400 text-white border-0">
                      <Crown className="h-3 w-3 mr-1" />
                      PRO
                    </Badge>
                  </motion.div>
                )}

                {/* Provider Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="p-4 bg-blue-50 rounded-xl text-center"
                  >
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <Users className="h-5 w-5 text-blue-500" />
                    </div>
                    <p className="text-2xl font-bold text-blue-700">
                      {booking?.providers_notified || 0}
                    </p>
                    <p className="text-xs text-blue-600">
                      {t('booking.status.providersNotified') || 'Providers Notified'}
                    </p>
                  </motion.div>
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="p-4 bg-green-50 rounded-xl text-center"
                  >
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <MessageSquare className="h-5 w-5 text-green-500" />
                    </div>
                    <p className="text-2xl font-bold text-green-700">
                      {booking?.providers_responded || 0}
                    </p>
                    <p className="text-xs text-green-600">
                      {t('booking.status.responses') || 'Responses'}
                    </p>
                  </motion.div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Responses List */}
        <AnimatePresence>
          {booking?.responses && booking.responses.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Sparkles className="h-5 w-5 text-yellow-500" />
                    {t('booking.status.providerResponses') || 'Provider Responses'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {booking.responses.map((response, idx) => (
                    <motion.div 
                      key={idx}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className={`p-4 rounded-xl border-2 ${
                        ['confirm', 'accept'].includes(response.response_type) 
                          ? 'bg-green-50 border-green-200' 
                          : ['alternative', 'propose'].includes(response.response_type)
                          ? 'bg-blue-50 border-blue-200'
                          : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-slate-900">{response.provider_name}</p>
                          {['confirm', 'accept'].includes(response.response_type) && (
                            <Badge className="bg-green-100 text-green-700 mt-2">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              {t('booking.response.confirmed') || 'Confirmed'}
                            </Badge>
                          )}
                          {['alternative', 'propose'].includes(response.response_type) && (
                            <div className="mt-2">
                              <Badge className="bg-blue-100 text-blue-700">
                                {t('booking.response.alternative') || 'Alternative Time'}
                              </Badge>
                              <p className="text-sm mt-1 text-slate-600">
                                {response.alternative_date} {response.alternative_time}
                              </p>
                            </div>
                          )}
                          {response.response_type === 'decline' && (
                            <Badge className="bg-red-100 text-red-700 mt-2">
                              {t('booking.response.declined') || 'Declined'}
                            </Badge>
                          )}
                        </div>
                        {response.estimated_price && (
                          <div className="text-right">
                            <p className="text-xl font-bold text-slate-900">
                              €{response.estimated_price}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {t('booking.response.estimate') || 'Estimate'}
                            </p>
                          </div>
                        )}
                      </div>
                      {response.message && (
                        <p className="mt-3 text-sm text-slate-600 italic bg-white p-3 rounded-lg">
                          "{response.message}"
                        </p>
                      )}
                    </motion.div>
                  ))}
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Waiting for responses */}
        {booking?.status === 'pending' && (!booking.responses || booking.responses.length === 0) && !showInitialSuccess && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <EmptyStateEncouragement 
              type="no_responses" 
              language={language}
            />
            <MotivationalTip 
              language={language} 
              context="waiting" 
              className="mt-4 text-center justify-center"
            />
          </motion.div>
        )}

        {/* Back to home */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-8 text-center"
        >
          <Link to="/booking">
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              {t('booking.status.newBooking') || 'Create Another Booking'}
            </Button>
          </Link>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="border-t bg-white py-6 mt-12">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <p className="text-sm text-muted-foreground flex items-center justify-center gap-1">
            <Shield className="h-4 w-4 text-green-500" />
            {t('booking.privacyNote') || 'Your contact info is shared only with matched providers'}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BookingStatusPage;
